import { useEffect, useState } from "react";
import type { ChatMessage, persistence } from "borogove";

export type AttachmentsMessage = Pick<
	ChatMessage,
	"senderId" | "localId" | "serverId" | "account" | "chatId"
>;

export const attachmentCollapseKey = (messages: AttachmentsMessage[]) =>
	[
		"ui#collapsedAttachments",
		...messages.flatMap((m) => [
			m.account(),
			m.chatId(),
			m.senderId,
			m.localId,
			m.serverId,
		]),
	].join("/");

export function useCollapsedAttachments(
	persistence: persistence.KeyValueStore,
	attachmentMessage: AttachmentsMessage,
	childMessage?: AttachmentsMessage,
) {
	const key = attachmentCollapseKey(
		[attachmentMessage, childMessage].filter((m) => m !== undefined),
	);

	const [collapsed, setCollapsed] = useState<boolean>(false);

	useEffect(() => {
		persistence
			.get(key)
			.then((value) => {
				setCollapsed(value === "collapsed");
			})
			.catch((error) => {
				console.error("Could not read attachment visibility", error);
				setCollapsed(false);
			});

		return () => {};
	}, [persistence, key]);

	const setCollapsedWithPersistence = (collapsed: boolean) => {
		setCollapsed(collapsed);
		return persistence
			.set(key, collapsed ? "collapsed" : null)
			.then((saved) => {
				if (!saved) console.error("Could not save attachment visibility");
			})
			.catch((error) => {
				console.error("Could not save attachment visibility", error);
			});
	};

	return [collapsed, setCollapsedWithPersistence] as const;
}
